import { useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { useParams } from "react-router-dom";
import useCompetitionLeaderboard from "../../components/CompetitionScoreboard/hooks/useCompetitionLeaderboard";
import useCompetitionMaster from "../../components/CompetitionScoreboard/hooks/useCompetitionMaster";
import useGolfCourseHoleDetails from "../../components/CompetitionScoreboard/hooks/useGolfCourseHoleDetails";
import useGolfGeneralScores from "../../components/CompetitionScoreboard/hooks/useGolfGeneralScores";
import { GeneralScore } from "../../Types/GeneralScore";
import { Participant } from "../../Types/Participant";
import { HoleDetail } from "../../Types/HoleDetail";
import { CompetitionMaster } from "../../Types/CompetitionMaster";
import { Button } from "./CompetitionScore.style";
import useCompetitionHandicapCategories from "../../components/CompetitionScoreboard/hooks/useCompetitionCategories";
import { HandicapCategory } from "../../Types/HandicapCategory";
import { formatDate } from "../../utils/date-utils";

function CompetitionScorePage() {
  const { competitionId = "" } = useParams();
  const [categoryType, setCategoryType] = useState<string | null>(null);
  const {
    competitionMaster,
    isLoading: isMasterLoading,
    error: masterError,
  } = useCompetitionMaster(competitionId);

  const [isGross, setIsGross] = useState<boolean | null>(null);
  useEffect(() => {
    if (competitionMaster) {
      const temp =
        competitionMaster?.secretHolesRevealed &&
        competitionMaster?.scoringStatus === 2;
      setIsGross(!temp);
    } else {
    }
  }, [competitionMaster]);
  const {
    participants,
    isLoading: isLeaderboardLoading,
    error: leaderboardError,
  } = useCompetitionLeaderboard(
    competitionMaster?.objectId ?? "",
    isGross,
    categoryType,
    0
  );

  const uniqueRoundIds: string[] = useMemo(
    () => [
      ...new Set(
        (participants ?? [])
          .map((item) => item?.roundId)
          .filter((id) => id !== undefined)
      ),
    ],
    [participants]
  );
  const secretHoles: Set<string> = useMemo(() => {
    if (!competitionMaster) return new Set<string>();
    return new Set(
      competitionMaster.secretHoles
        ?.map((item) => item.holeNumber)
        .filter(
          (holeNumber): holeNumber is string => holeNumber !== undefined
        ) || []
    );
  }, [competitionMaster]);

  const {
    golfCourseHoleDetails,
    isLoading: isHoleLoading,
    error: holeError,
  } = useGolfCourseHoleDetails(competitionMaster?.golfCourse?.gId);

  const {
    golfGeneralScores,
    isLoading: isScoresLoading,
    error: scoresError,
  } = useGolfGeneralScores(uniqueRoundIds);

  const {
    handicapCategories,
    isLoading: isHandicapCategoriesLoading,
    error: handicapCategoriesError,
  } = useCompetitionHandicapCategories(competitionId);

  const isLoading = isMasterLoading || isHoleLoading;
  const error = masterError || holeError || leaderboardError || scoresError;

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    console.log(error);

    const errorMessage =
      (error as any)?.response?.data?.message ?? "Something went wrong!";

    return <div>Error: {errorMessage}</div>;
  }

  const data =
    competitionMaster?.competingAs === 0
      ? transformData(golfGeneralScores, participants, secretHoles)
      : transformDataTeams(golfGeneralScores, participants, secretHoles);
  const parData = transformPar(golfCourseHoleDetails, secretHoles);
  const teeData = transformTees(golfCourseHoleDetails);
  const headers = generateHeaders(
    secretHoles,
    competitionMaster ?? null,
    isGross
  );

  const date = formatDate(new Date(competitionMaster?.startDate?.iso || ""));

  return (
    <div style={{ padding: "0.0em 1em" }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h4>{competitionMaster?.golfCourse?.gcName ?? "Golf Course"}</h4>
        <h2 style={{ textAlign: "center" }}>
          Scoreboard: {competitionMaster?.name}
        </h2>
        <h4>{date}</h4>
      </div>
      <div
        style={{
          display: "flex",
          gap: "0.5em",
          padding: "1em",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "0.5em",
          }}
        >
          <Button
            selected={Boolean(isGross)}
            onClick={(e) => {
              e.preventDefault();
              setIsGross(true);
              setCategoryType(null);
            }}
          >
            Gross
          </Button>
          {competitionMaster?.secretHolesRevealed &&
            competitionMaster?.scoringStatus === 2 && (
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  setIsGross(false);
                }}
                selected={!isGross}
              >
                Double Peoria
              </Button>
            )}
        </div>
        <CSVLink
          data={[
            parData,
            ...teeData,
            ...data.sort((a, b) =>
              (a?.name ?? "").localeCompare(b?.name ?? "")
            ),
          ]}
          headers={headers}
          filename={`${competitionMaster?.name}.csv`}
        >
          <button
            style={{
              padding: "10px 20px",
              // margin: "20px auto",
              cursor: "pointer",
              borderRadius: "5px",
              background: "#008738",
              color: "#fff",
              border: "none",
            }}
          >
            Download
          </button>
        </CSVLink>
      </div>
      {!isGross && (
        <div style={{ display: "flex", gap: "0.5em", padding: "0em 1em" }}>
          <Button
            selected={categoryType == null}
            onClick={(e) => {
              e.preventDefault();
              setCategoryType(null);
            }}
          >
            All
          </Button>
          {competitionMaster?.handicapCategories != null &&
            competitionMaster?.handicapCategories?.map((obj: any) => (
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  setCategoryType(obj.type?.toString() ?? null);
                }}
                selected={obj.type == categoryType}
                key={obj.type}
              >
                {obj.typeName}
              </Button>
            ))}

          {competitionMaster?.handicapCategories == null &&
            handicapCategories?.map((obj: HandicapCategory) => (
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  setCategoryType(obj.objectId);
                }}
                selected={obj.objectId === categoryType}
                key={obj.type}
              >
                {obj.typeName}
              </Button>
            ))}
        </div>
      )}
      {isLeaderboardLoading ? (
        <>Loading</>
      ) : (
        <>
          <div className="table-container">
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "20px",
              }}
            >
              <thead>
                <tr>
                  {headers.map((header) => {
                    return (
                      <th
                        key={header.key}
                        style={{
                          border: "1px solid #ccc",
                          padding: "8px",
                          background: "#f7f7f7",
                        }}
                      >
                        {parData[header.key]}
                      </th>
                    );
                  })}
                </tr>
                <tr>
                  {headers.map((header) => (
                    <th
                      key={header.key}
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        background: header.isSecret ? "#ff7d7d" : "#f7f7f7",
                      }}
                    >
                      {header.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ...teeData,
                  ...data.sort((a, b) => {
                    const aNum = parseInt(
                      (a.teamName ?? "").replace("Team ", ""),
                      10
                    );
                    const bNum = parseInt(
                      (b.teamName ?? "").replace("Team ", ""),
                      10
                    );
                    return aNum - bNum;
                  }),
                ].map((row, index) => (
                  <tr key={index}>
                    {headers.map((header) => (
                      <td
                        key={header.key}
                        style={{
                          border: "1px solid #ccc",
                          padding: "8px",
                          textAlign: "center",
                          background: String(row?.bgColor).toLowerCase(),
                          color: String(row?.color).toLowerCase(),
                        }}
                      >
                        {row[header.key] ?? "-"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default CompetitionScorePage;

const generateHeaders = (
  secretHoles: Set<string>,
  competitionMaster: CompetitionMaster | null,
  isGross: boolean | null
): any[] => {
  const showSecretHoles =
    !isGross &&
    competitionMaster?.secretHolesRevealed &&
    competitionMaster?.scoringStatus === 2;

  const headers: any[] = [
    { label: "Name", key: "name" },
    // { label: "CR", key: "courseRating" },
    // { label: "SR", key: "slopeRating" },
  ];

  for (let i = 1; i <= 9; i++) {
    headers.push({
      label: `Hole ${i}${
        showSecretHoles && secretHoles.has(`${i}`) ? "(HH)" : ""
      }`,
      key: `${i}`,
      isSecret: showSecretHoles && secretHoles.has(`${i}`),
    });
  }

  headers.push(
    { label: "Out", key: "out" }
    // { label: "", key: "" }
  );

  for (let i = 10; i <= 18; i++) {
    headers.push({
      label: `Hole ${i}${
        showSecretHoles && secretHoles.has(`${i}`) ? "(HH)" : ""
      }`,
      key: `${i}`,
      isSecret: showSecretHoles && secretHoles.has(`${i}`),
    });
  }

  headers.push(
    { label: "In", key: "in" },
    // { label: "", key: "" },
    { label: "Total", key: "total" },
    // { label: "Handicap", key: "handicap" },

    ...(showSecretHoles
      ? [
          { label: "Secret Hole Total", key: "secretTotal" },
          { label: "Net Adjusted Handicap (Round off)", key: "peoriaHandicap" },
          { label: "DP Net", key: "peoriaNetScore" },
        ]
      : []),
    { label: "Net Score Total", key: "netScore" }
  );

  return headers;
};

type TransformedData = {
  [key: string]: number | string | undefined;
  name?: string;
  handicap?: number;
  peoriaHandicap?: number;
  peoriaNetScore?: number;
  total?: number;
  in?: number;
  out?: number;
  secretTotal?: number;
  teamName?: string;
};

const transformData = (
  scores: GeneralScore[],
  participants: Participant[],
  secretHoles: Set<string>
): TransformedData[] => {
  const scoresMap = scores.reduce<Record<string, GeneralScore[]>>(
    (acc, score) => {
      if (!acc[score.user_id]) {
        acc[score.user_id] = [];
      }
      acc[score.user_id].push(score);
      return acc;
    },
    {}
  );

  return participants.map((participant) => {
    const {
      userId,
      userName,
      handicap,
      peoriaHandicap,
      thru,
      points: {
        PeoriaNetScore,
        GrossScoreTotal,
        GrossBack9,
        NetScoreTotal,
      } = {},
    } = participant;

    const transform: Record<string, any> = {
      name: `${userName}(${thru})`,
      handicap,
      peoriaHandicap,
      peoriaNetScore: PeoriaNetScore,
      // total: GrossScoreTotal,
      // in: GrossBack9,
      // out: (GrossScoreTotal ?? 0) - (GrossBack9 ?? 0),
      total: 0,
      in: 0,
      out: 0,
      secretTotal: 0,
      netScore: NetScoreTotal,
    };

    const participantScores = userId ? scoresMap[userId] ?? [] : [];
    participantScores.forEach((score) => {
      const { holeId, gross_score } = score;
      const keyName = `${holeId.holeNumber}`;
      transform[keyName] = gross_score;
    });

    transform.out = Object.keys(transform)
      .filter((key) => Number(key) >= 1 && Number(key) <= 9)
      .reduce((sum, key) => sum + transform[key], 0);

    transform.in = Object.keys(transform)
      .filter((key) => Number(key) >= 10 && Number(key) <= 18)
      .reduce((sum, key) => sum + transform[key], 0);

    transform.total = transform.in + transform.out;

    transform.secretTotal = Object.keys(transform)
      .filter((key) => secretHoles.has(key))
      .reduce((sum, key) => sum + transform[key], 0);

    return transform;
  });
};

const transformDataTeams = (
  scores: GeneralScore[],
  participants: Participant[],
  secretHoles: Set<string>
): TransformedData[] => {
  const scoresMap = scores.reduce<Record<string, GeneralScore[]>>(
    (acc, score) => {
      if (!acc[score.user_id]) {
        acc[score.user_id] = [];
      }
      acc[score.user_id].push(score);
      return acc;
    },
    {}
  );

  return participants.flatMap((participant1) => {
    return participant1?.teamMembers?.map((participant: Participant) => {
      const {
        userId,
        userName,
        handicap,
        peoriaHandicap,
        thru,

        points: {
          PeoriaNetScore,
          GrossScoreTotal,
          GrossBack9,
          NetScoreTotal,
        } = {},
      } = participant;

      const transform: Record<string, any> = {
        name: `${userName}(${participant1?.userName})`,
        handicap,
        peoriaHandicap,
        peoriaNetScore: PeoriaNetScore,
        // total: GrossScoreTotal,
        // in: GrossBack9,
        // out: (GrossScoreTotal ?? 0) - (GrossBack9 ?? 0),
        total: 0,
        in: 0,
        out: 0,
        secretTotal: 0,
        netScore: NetScoreTotal,
        teamName: participant1?.userName,
      };

      const participantScores = userId ? scoresMap[userId] ?? [] : [];
      participantScores.forEach((score) => {
        const { holeId, gross_score, net_score } = score;
        const keyName = `${holeId.holeNumber}`;
        transform[keyName] = gross_score;
      });

      transform.netScore = participantScores.reduce((acc, score) => {
        return acc + score.net_score;
      }, 0);

      transform.out = Object.keys(transform)
        .filter((key) => Number(key) >= 1 && Number(key) <= 9)
        .reduce((sum, key) => sum + transform[key], 0);

      transform.in = Object.keys(transform)
        .filter((key) => Number(key) >= 10 && Number(key) <= 18)
        .reduce((sum, key) => sum + transform[key], 0);

      transform.total = transform.in + transform.out;

      transform.secretTotal = Object.keys(transform)
        .filter((key) => secretHoles.has(key))
        .reduce((sum, key) => sum + transform[key], 0);

      return transform;
    });
  });
};

function transformTees(data: HoleDetail[]): TransformedData[] {
  const transformed: TransformedData[] = [];

  const teeGroups: Record<string, TransformedData> = {};

  data.forEach((hole) => {
    hole.tees?.forEach((tee) => {
      if (!teeGroups[tee.teeBoxName ?? ""]) {
        teeGroups[tee.teeBoxName ?? ""] = {
          name: tee.teeBoxName ?? "",
          total: tee.total18Yards,
          in: tee.back9YardsTotal,
          out: tee.front9YardsTotal,
          courseRating: tee.courseRating,
          slopeRating: tee.slopeRating,
          bgColor: tee.teeBoxBackColorCode,
          color: tee.teeBoxFontColorCode,
        };
      }

      teeGroups[tee.teeBoxName ?? ""][hole.holeNumber ?? ""] = tee.noOfYards;
    });
  });

  for (const key in teeGroups) {
    transformed.push(teeGroups[key]);
  }
  return transformed;
}
function transformPar(
  data: HoleDetail[],
  secretHoles: Set<string>
): TransformedData {
  const transformed: TransformedData = {
    name: "Par",
    secretTotal: 0,
    out: 0,
    in: 0,
    total: 0,
  };
  data.forEach((hole) => {
    if (hole.holeNumber !== undefined) {
      transformed[hole.holeNumber] = hole.par;
      if (secretHoles.has(hole.holeNumber)) {
        transformed.secretTotal =
          (transformed.secretTotal ?? 0) + (hole.par ?? 0);
      }
      if (Number(hole.holeNumber) <= 9) {
        transformed.out = (transformed.out ?? 0) + (hole.par ?? 0);
      } else if (Number(hole.holeNumber) > 9) {
        transformed.in = (transformed.in ?? 0) + (hole.par ?? 0);
      }
      transformed.total = (transformed.total ?? 0) + (hole.par ?? 0);
    }
  });
  return transformed;
}
